import { fetchJsonItems } from './_utils.js';

/** Japan calendar dates; timestamps without an offset are interpreted as JST. */
export function parseScheduleDate(value) {
  if (typeof value !== 'string') return NaN;
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(Z|[+-]\d{2}:?\d{2})?)?$/
  );
  if (!match) return NaN;
  const [, year, month, day, hour = '00', minute = '00', second = '00'] = match;
  const civil = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (
    civil.getUTCFullYear() !== Number(year) ||
    civil.getUTCMonth() + 1 !== Number(month) ||
    civil.getUTCDate() !== Number(day) ||
    Number(hour) > 23 ||
    Number(minute) > 59 ||
    Number(second) > 59
  )
    return NaN;
  const normalized = value.replace(' ', 'T');
  return Date.parse(match[4] ? normalized + (match[7] ? '' : '+09:00') : `${value}T00:00:00+09:00`);
}
export function selectUpcomingItems(items, options = {}, now = new Date()) {
  const days = Math.max(1, Math.min(365, Number(options.days ?? options.filterDays) || 30));
  const limit = Math.max(1, Math.min(100, Number(options.maxDisplayItems) || 3));
  const japan = new Date(now.getTime() + 9 * 3600000);
  const start =
    Date.UTC(japan.getUTCFullYear(), japan.getUTCMonth(), japan.getUTCDate()) - 9 * 3600000;
  const end = start + days * 86400000;
  const seen = new Set();
  return items
    .filter((item) => {
      if (!item || !item.id || !item.teamName || seen.has(String(item.id))) return false;
      const time = parseScheduleDate(item.timeStamp);
      if (!(time >= start && time < end)) return false;
      seen.add(String(item.id));
      return true;
    })
    .sort((a, b) => parseScheduleDate(a.timeStamp) - parseScheduleDate(b.timeStamp))
    .slice(0, limit);
}

export function loadAndFilterItemsByDate(
  jsonUrl = '/assets/json/itemListDate.json',
  targetSelector = '.c-data-area__date',
  options = {}
) {
  const contentArea = document.querySelector(targetSelector);
  if (!contentArea) {
    return;
  }

  // デフォルトオプションとマージ
  const settings = {
    days: options.days ?? options.filterDays ?? 30,
    maxDisplayItems: 3, // 表示する最大件数
    noDataMessage: `今後${options.days ?? options.filterDays ?? 30}日間の予定はありません。`,
    ...options,
  };

  fetchJsonItems(jsonUrl)
    .then((allItems) => {
      // オブジェクトの値を配列として取得

      const itemsToDisplay = selectUpcomingItems(allItems, settings);

      // すべてのHTMLを格納するフラグメント
      const fragment = document.createDocumentFragment();

      if (itemsToDisplay.length === 0) {
        const message = document.createElement('p');
        message.className = 'no-schedule-message';
        message.textContent = settings.noDataMessage;
        contentArea.replaceChildren(message);
      } else {
        itemsToDisplay.forEach((itemData) => {
          const articleElement = createScheduleArticle(itemData); // ヘルパー関数で要素を生成
          if (articleElement) {
            fragment.appendChild(articleElement);
          }
        });
        contentArea.replaceChildren(fragment); // 一度だけDOMに追加
      }
    })
    .catch((error) => {
      console.error('Error loading JSON:', error);
      contentArea.innerHTML =
        '<p class="json-load-error">スケジュールデータの読み込みに失敗しました。</p>';
    });
}

/**
 * 個々のスケジュールアイテムの <article> 要素を生成するヘルパー関数。
 * @param {object} itemData スケジュールアイテムのデータオブジェクト。
 * @returns {HTMLElement} 生成された <article> 要素。
 */
function createScheduleArticle(itemData) {
  // 必須データチェック
  if (
    !itemData ||
    typeof itemData !== 'object' ||
    !itemData.id ||
    !itemData.teamName ||
    !itemData.timeStamp
  ) {
    return null;
  }

  const article = document.createElement('article');
  // IDはページ内でユニークにするために投稿IDなどを加えるのが望ましい
  // ここでは itemData.id に 'match' を付与していましたが、今回は 'schedule-' を付与
  article.id = `schedule-${itemData.id}`;
  article.className = 'c-data-area__article';

  // ホストアイコン
  const iconHost = document.createElement('p');
  iconHost.className = itemData.host ? itemData.host : ''; // 存在しない場合は空
  iconHost.textContent = itemData.hostLabel ? itemData.hostLabel : '';
  article.appendChild(iconHost);

  // VSチーム名
  const vsTeam = document.createElement('h3');
  vsTeam.textContent = itemData.teamName ? itemData.teamName : '';
  article.appendChild(vsTeam);

  // スケジュール (日付)
  const schedule = document.createElement('p');
  // 日付部品の作成 (より堅牢にするため、Dateオブジェクトから取得)
  const itemDate = new Date(parseScheduleDate(itemData.timeStamp) + 9 * 3600000);
  if (!isNaN(itemDate.getTime())) {
    // 有効な日付か確認
    const yearSpan = document.createElement('span');
    yearSpan.textContent = itemDate.getUTCFullYear() + '年';

    const monthSpan = document.createElement('span');
    monthSpan.textContent = itemDate.getUTCMonth() + 1 + '月'; // 月は0-11なので+1

    const dateSpan = document.createElement('span');
    dateSpan.textContent = itemDate.getUTCDate() + '日';

    const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][itemDate.getUTCDay()];
    const dowSpan = document.createElement('span');
    dowSpan.textContent = `(${dayOfWeek})`;

    schedule.appendChild(yearSpan);
    schedule.appendChild(monthSpan);
    schedule.appendChild(dateSpan);
    schedule.appendChild(dowSpan);
  } else {
    schedule.textContent = itemData.timeStamp ? itemData.timeStamp : ''; // 日付が不正なら元の文字列をフォールバック
  }
  article.appendChild(schedule);

  // 時間と場所 (既存の構造を維持しつつ、安全化)
  const time = document.createElement('p');
  time.textContent = itemData.time ? itemData.time : '';
  article.appendChild(time);

  const place = document.createElement('p');
  place.textContent = itemData.place ? itemData.place : '';
  article.appendChild(place);

  return article;
}
